import { useState, useEffect } from "react";
import { Box, Container, Heading, Button, Text, Link as CLink, VStack, Card, Stack } from "@chakra-ui/react";
import { Link } from "@inertiajs/react";
import { my_notifications_path, my_settings_path } from "@/routes";

export default function Notifications() {
  const [permission, setPermission] = useState<NotificationPermission>("default");
  const [subscription, setSubscription] = useState<PushSubscription | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  // ArrayBufferをBase64文字列に変換する関数
  const arrayBufferToBase64 = (buffer: ArrayBuffer | null): string => {
    if (!buffer) return "";
    const bytes = new Uint8Array(buffer);
    let binary = "";
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  };

  useEffect(() => {
    if ("Notification" in window) {
      setPermission(Notification.permission);
    }

    // 既存の購読状態を確認
    checkExistingSubscription();
  }, []);

  const checkExistingSubscription = () => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.ready
        .then(registration => {
          return registration.pushManager.getSubscription();
        })
        .then(existingSubscription => {
          setSubscription(existingSubscription);
        })
        .catch(error => {
          console.error("Failed to check existing subscription:", error);
        });
    }
  };

  const handleRequestPermission = () => {
    if ("Notification" in window) {
      Notification.requestPermission()
        .then(result => {
          setPermission(result);

          if (result === "granted") {
            // テスト通知
            new Notification("通知設定完了", {
              body: "通知が許可されました！",
            });
          }
        })
        .catch(error => {
          console.error("Failed to request permission:", error);
        });
    }
  };

  const handleSubscribePush = () => {
    if (!window.vapidPublicKey) {
      console.error("VAPID public key not found");
      return;
    }

    setSubscriptionLoading(true);
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.ready
        .then(registration => {
          return registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: window.vapidPublicKey,
          });
        })
        .then(newSubscription => {
          setSubscription(newSubscription);
          // サーバーに購読情報を送信
          return saveSubscription(newSubscription);
        })
        .then(() => {
          // 購読完了の通知
          new Notification("Push通知設定完了", {
            body: "Push通知の購読が完了しました!",
          });
        })
        .catch(error => {
          console.error("Push subscription failed:", error);
        })
        .finally(() => {
          setSubscriptionLoading(false);
        });
    }
  };

  const handleUnsubscribePush = () => {
    if (!subscription) return;

    const endpoint = subscription.endpoint;

    setSubscriptionLoading(true);
    subscription
      .unsubscribe()
      .then(() => {
        // サーバーから購読情報を削除
        return removeSubscription(endpoint);
      })
      .then(() => {
        setSubscription(null);
        console.log("Push subscription removed");
      })
      .catch(error => {
        console.error("Push unsubscription failed:", error);
      })
      .finally(() => {
        setSubscriptionLoading(false);
      });
  };

  // サーバーに購読情報を送信
  const saveSubscription = async (subscription: PushSubscription) => {
    const data = {
      endpoint: subscription.endpoint,
      p256dh: arrayBufferToBase64(subscription.getKey("p256dh")),
      auth: arrayBufferToBase64(subscription.getKey("auth")),
    };

    return fetch(my_notifications_path(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "",
      },
      body: JSON.stringify(data),
    })
      .then(response => response.json())
      .then(result => {
        if (result.status === "error") {
          throw new Error(result.message);
        }
        return result;
      });
  };

  // サーバーから購読情報を削除
  const removeSubscription = async (endpoint: string) => {
    return fetch(my_notifications_path({ endpoint: endpoint }), {
      method: "DELETE",
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "",
      },
    })
      .then(response => response.json())
      .then(result => {
        if (result.status === "error") {
          throw new Error(result.message);
        }
        return result;
      });
  };

  return (
    <Container maxW="2xl" py={8}>
      <VStack gap={8} align="stretch">
        {/* Header */}
        <Box>
          <CLink as={Link} href={my_settings_path()} fontSize="sm">
            ← 設定に戻る
          </CLink>
          <Heading size="2xl" mt={4}>
            通知設定
          </Heading>
          <Text color="gray.600" mt={2}>
            通知の受け取り方法を設定してください
          </Text>
        </Box>

        {/* Main Card */}
        <Card.Root bg="white" shadow="lg" borderRadius="xl">
          <Card.Body p={8}>
            <Stack gap={6}>
              {/* WebPush Notification Setting */}
              <Box p={6} borderRadius="lg" bg="blue.50" border="1px solid" borderColor="blue.100">
                <VStack align="start" gap={4}>
                  <Box>
                    <Heading size="md" color="gray.800" mb={2}>
                      🔔 Push通知
                    </Heading>
                    <Text fontSize="sm" color="gray.600" mb={4}>
                      ブラウザを通じてリアルタイムで通知を受け取ることができます。
                    </Text>
                  </Box>

                  <Box w="full">
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      p={4}
                      bg="white"
                      borderRadius="lg"
                      border="1px solid"
                      borderColor="gray.200"
                    >
                      <Box>
                        <Text fontWeight="semibold" fontSize="sm" color="gray.800">
                          Push通知の状態
                        </Text>
                        <Text fontSize="xs" color="gray.500" mt={1}>
                          {permission === "granted" && subscription && "購読中 - 通知を受け取ります"}
                          {permission === "granted" && !subscription && "通知許可済み - 未購読"}
                          {permission === "denied" && "通知がブロックされています"}
                          {permission === "default" && "通知の許可が設定されていません"}
                        </Text>
                      </Box>

                      {/* default状態 - 通知を許可ボタン */}
                      {permission === "default" && (
                        <Button onClick={handleRequestPermission} colorPalette="blue" size="sm" borderRadius="lg">
                          通知を許可
                        </Button>
                      )}

                      {/* denied状態 - ブロック中バッジ */}
                      {permission === "denied" && (
                        <Box bg="red.100" color="red.800" px={3} py={1} borderRadius="full" fontSize="xs" fontWeight="semibold">
                          ❌ ブロック中
                        </Box>
                      )}

                      {/* granted状態 - 購読/解除ボタンまたは購読中バッジ */}
                      {permission === "granted" && !subscription && (
                        <Button
                          onClick={handleSubscribePush}
                          loading={subscriptionLoading}
                          loadingText="購読中..."
                          colorPalette="blue"
                          size="sm"
                          borderRadius="lg"
                          disabled={!window.vapidPublicKey}
                        >
                          購読する
                        </Button>
                      )}

                      {permission === "granted" && subscription && (
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box bg="green.100" color="green.800" px={3} py={1} borderRadius="full" fontSize="xs" fontWeight="semibold">
                            ✓ 購読中
                          </Box>
                          <Button
                            onClick={handleUnsubscribePush}
                            loading={subscriptionLoading}
                            loadingText="解除中..."
                            colorPalette="red"
                            variant="outline"
                            size="xs"
                            borderRadius="lg"
                          >
                            解除
                          </Button>
                        </Box>
                      )}
                    </Box>
                  </Box>

                  {/* 状態に応じた説明セクション */}
                  {permission === "granted" && subscription && (
                    <Box p={4} bg="gradient-to-r from-green.50 to-blue.50" borderRadius="lg" border="1px solid" borderColor="green.100">
                      <Text fontSize="xs" color="gray.700" mb={2} fontWeight="semibold">
                        ✓ Push通知設定完了
                      </Text>
                      <VStack gap={1} align="start">
                        <Text fontSize="xs" color="gray.600">
                          • Push通知を購読中です
                        </Text>
                        <Text fontSize="xs" color="gray.600">
                          • ゲームやイベントの更新を自動でお知らせします
                        </Text>
                      </VStack>
                    </Box>
                  )}

                  {permission === "granted" && !subscription && (
                    <Box p={4} bg="gradient-to-r from-blue.50 to-purple.50" borderRadius="lg" border="1px solid" borderColor="blue.100">
                      <Text fontSize="xs" color="gray.700" mb={2} fontWeight="semibold">
                        📱 Push通知を開始しませんか？
                      </Text>
                      <VStack gap={1} align="start">
                        <Text fontSize="xs" color="gray.600">
                          • 通知は許可済みです
                        </Text>
                        <Text fontSize="xs" color="gray.600">
                          • 「購読する」ボタンでPush通知を開始できます
                        </Text>
                        <Text fontSize="xs" color="gray.600">
                          • ゲームやイベントの更新をリアルタイムでお知らせします
                        </Text>
                      </VStack>
                    </Box>
                  )}

                  {permission === "denied" && (
                    <Box p={4} bg="gradient-to-r from-red.50 to-orange.50" borderRadius="lg" border="1px solid" borderColor="red.100">
                      <Text fontSize="xs" color="gray.700" mb={2} fontWeight="semibold">
                        ⚠️ 通知がブロックされています
                      </Text>
                      <VStack gap={1} align="start">
                        <Text fontSize="xs" color="gray.600">
                          • 一度ブロックされた通知は、ブラウザ設定から手動で許可する必要があります
                        </Text>
                        <Text fontSize="xs" color="gray.600">
                          • アドレスバーの🔒アイコン → 「通知」を「許可」に変更
                        </Text>
                        <Text fontSize="xs" color="gray.600">
                          • または、ブラウザの設定画面から当サイトの通知を許可してください
                        </Text>
                      </VStack>
                    </Box>
                  )}

                  {permission === "default" && (
                    <Box p={4} bg="gradient-to-r from-blue.50 to-purple.50" borderRadius="lg" border="1px solid" borderColor="blue.100">
                      <Text fontSize="xs" color="gray.700" mb={2} fontWeight="semibold">
                        💡 通知を受け取るために
                      </Text>
                      <VStack gap={1} align="start">
                        <Text fontSize="xs" color="gray.600">
                          • 「通知を許可」ボタンをクリックしてください
                        </Text>
                        <Text fontSize="xs" color="gray.600">
                          • ブラウザから許可の確認が表示されます
                        </Text>
                      </VStack>
                    </Box>
                  )}
                </VStack>
              </Box>

              {/* Browser Support Info */}
              {"Notification" in window ? (
                <Box p={4} bg="gray.50" borderRadius="lg" border="1px solid" borderColor="gray.200">
                  <Text fontSize="xs" color="gray.600" fontWeight="semibold" mb={2}>
                    ℹ️ ブラウザサポート
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    お使いのブラウザはWebPush通知をサポートしています。
                  </Text>
                </Box>
              ) : (
                <Box p={4} bg="orange.50" borderRadius="lg" border="1px solid" borderColor="orange.200">
                  <Text fontSize="xs" color="orange.800" fontWeight="semibold" mb={2}>
                    ⚠️ ブラウザ非対応
                  </Text>
                  <Text fontSize="xs" color="orange.700">
                    お使いのブラウザはWebPush通知をサポートしていません。
                  </Text>
                </Box>
              )}
            </Stack>
          </Card.Body>
        </Card.Root>
      </VStack>
    </Container>
  );
}
